import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import SEOHead from '../components/SEOHead';
import { LogoText } from '../components/ui/Logo';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

type PageState = 'form' | 'submitting' | 'success' | 'error';

export default function UnsubscribePage() {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const tokenParam = searchParams.get('token')?.trim() ?? '';

  const [email, setEmail] = useState('');
  const [state, setState] = useState<PageState>(tokenParam ? 'submitting' : 'form');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const submitUnsubscribe = useCallback(
    async (payload: { email?: string; token?: string }) => {
      setState('submitting');
      setErrorMessage(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/marketing/public-unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Memara-Client': 'ui',
          },
          body: JSON.stringify(payload),
        });
        if (res.status === 429) {
          setState('error');
          setErrorMessage(t('unsubscribe.rateLimited'));
          return;
        }
        if (!res.ok && res.status !== 200) {
          const data = await res.json().catch(() => ({}));
          const detail =
            typeof data?.detail === 'string'
              ? data.detail
              : t('unsubscribe.submitError');
          setState('error');
          setErrorMessage(detail);
          return;
        }
        setState('success');
      } catch {
        setState('error');
        setErrorMessage(t('unsubscribe.submitError'));
      }
    },
    [t]
  );

  useEffect(() => {
    if (tokenParam) {
      void submitUnsubscribe({ token: tokenParam });
    }
  }, [tokenParam, submitUnsubscribe]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setErrorMessage(t('unsubscribe.emailRequired'));
      setState('error');
      return;
    }
    void submitUnsubscribe({ email: trimmed });
  };

  return (
    <>
      <SEOHead
        title={t('unsubscribe.pageTitle')}
        description={t('unsubscribe.pageDescription')}
        keywords={['unsubscribe', 'email preferences', 'memara']}
        noIndex
      />
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <header className="border-b bg-white/80 backdrop-blur">
          <div className="max-w-lg mx-auto px-4 py-4">
            <LogoText variant="dark" size="md" iconSize={32} className="text-foreground" />
          </div>
        </header>

        <main className="max-w-lg mx-auto px-4 py-12">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <CardTitle>{t('unsubscribe.pageTitle')}</CardTitle>
                  <CardDescription>{t('unsubscribe.pageDescription')}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {state === 'success' && (
                <p className="text-sm text-gray-700 leading-relaxed">
                  {t('unsubscribe.successMessage')}
                </p>
              )}

              {state === 'submitting' && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('unsubscribe.processing')}
                </div>
              )}

              {state === 'error' && errorMessage && (
                <p className="text-sm text-red-600" role="alert">
                  {errorMessage}
                </p>
              )}

              {state === 'form' && !tokenParam && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <p className="text-sm text-gray-600">{t('unsubscribe.formHint')}</p>
                  <div className="space-y-2">
                    <Label htmlFor="unsubscribe-email">{t('unsubscribe.emailLabel')}</Label>
                    <Input
                      id="unsubscribe-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('unsubscribe.emailPlaceholder')}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    {t('unsubscribe.confirmButton')}
                  </Button>
                </form>
              )}

              {isAuthenticated && (
                <p className="text-sm text-gray-600 border-t pt-4">
                  {t('unsubscribe.loggedInHint')}{' '}
                  <Link
                    to="/settings/notifications"
                    className="text-indigo-600 hover:underline font-medium"
                  >
                    {t('unsubscribe.settingsLink')}
                  </Link>
                </p>
              )}

              <p className="text-xs text-gray-500">
                {t('unsubscribe.supportHint')}{' '}
                <a href="mailto:support@memara.io" className="text-indigo-600 hover:underline">
                  support@memara.io
                </a>
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    </>
  );
}
